/**
 * Command Parser Hook
 * Detects and parses @ commands in user input without sending to AI
 */

export interface ParsedCommand {
  type: 'open' | 'close' | 'edit' | 'plan' | 'block' | 'none';
  args: string[];
  rawInput: string;
  isCommand: boolean;
}

/**
 * Parse @ commands from user input
 * 
 * Examples:
 * - "@open system architecture" → { type: 'open', args: ['system architecture'], ... }
 * - "@edit 1" → { type: 'edit', args: ['1'], ... }
 * - "@close all" → { type: 'close', args: ['all'], ... }
 * - "@plan SVP" → { type: 'plan', args: ['SVP'], ... } - starts Plan mode for a document
 * - "@block 14" → { type: 'block', args: ['14'], ... } - reference block 14 in current document
 * - "@block sec-1-1:p-1-1-5" → { type: 'block', args: ['sec-1-1:p-1-1-5'], ... } - reference specific block
 * - "regular chat" → { type: 'none', args: [], rawInput: 'regular chat', isCommand: false }
 */
export function useCommandParser() {
  const parseCommand = (input: string): ParsedCommand => {
    const trimmedInput = input.trim();
    
    // Check if input starts with @
    if (!trimmedInput.startsWith('@')) {
      return {
        type: 'none',
        args: [],
        rawInput: input,
        isCommand: false
      };
    }
    
    // Extract command and arguments
    const parts = trimmedInput.substring(1).split(/\s+/);
    const command = parts[0].toLowerCase();
    const args = parts.slice(1);
    
    // Validate command type
    const validCommands = ['open', 'close', 'edit', 'plan', 'block', 'project'];
    if (!validCommands.includes(command)) {
      // Unknown command - treat as regular input
      return {
        type: 'none',
        args: [],
        rawInput: input,
        isCommand: false
      };
    }
    
    return {
      type: command as 'open' | 'close' | 'edit' | 'plan' | 'block' | 'project',
      args: args.length > 0 ? [args.join(' ')] : [], // Rejoin args for search queries
      rawInput: input,
      isCommand: true
    };
  };
  
  /**
   * Check if input is in the middle of typing a command
   * Used to trigger autocomplete
   */
  const isTypingCommand = (input: string, commandType: string): boolean => {
    // Don't trim - we need to preserve the space detection
    const commandPrefix = `@${commandType}`;
    
    // Check if starts with @command followed by a space
    const hasCommand = input.startsWith(commandPrefix);
    const hasSpace = input.length > commandPrefix.length && input[commandPrefix.length] === ' ';
    
    return hasCommand && hasSpace;
  };
  
  /**
   * Extract the query portion from a command
   * e.g., "@open system arch" → "system arch"
   */
  const extractQuery = (input: string, commandType: string): string => {
    const trimmedInput = input.trim();
    const commandPrefix = `@${commandType} `;
    
    if (trimmedInput.startsWith(commandPrefix)) {
      return trimmedInput.substring(commandPrefix.length);
    }
    
    return '';
  };
  
  return {
    parseCommand,
    isTypingCommand,
    extractQuery
  };
}

